import { differenceInCalendarDays, parseISO } from 'date-fns';

import type { Task } from '@/types/task';
import { TaskCategory } from '@/types/task';
import { getTaskStatus } from '@/utils/task-display-utils';
import { CATEGORY_CONFIG } from '@/utils/category-utils';

export type PrepFilter = 'all' | 'due' | 'overdue' | 'blocked';

/**
 * All 18 TaskCategory members in canonical enum order.
 * Used to preserve category display order in grouped task lists.
 */
export const CANONICAL_CATEGORY_ORDER: TaskCategory[] = [
  TaskCategory.FoodSource,
  TaskCategory.StandPrep,
  TaskCategory.TrailCameras,
  TaskCategory.Scouting,
  TaskCategory.GearEquipment,
  TaskCategory.PropertyInfrastructure,
  TaskCategory.HabitatManagement,
  TaskCategory.RegulatoryAdmin,
  TaskCategory.PostSeason,
  TaskCategory.PredatorControl,
  TaskCategory.WaterholeManagement,
  TaskCategory.WildlifeStructures,
  TaskCategory.MineralNutrition,
  TaskCategory.NeighborAccess,
  TaskCategory.HerdData,
  TaskCategory.MockScrapes,
  TaskCategory.TreePlanting,
  TaskCategory.Other,
];

// Sanity check: CATEGORY_CONFIG and CANONICAL_CATEGORY_ORDER must cover the same keys
// (compile-time enforced by the type system via the Record<TaskCategory, CategoryConfig>)
const _exhaustiveCheck: Record<TaskCategory, unknown> = CATEGORY_CONFIG;

function getDueDays(task: Task, today: Date): number {
  return differenceInCalendarDays(parseISO(task.dueDate), today);
}

/**
 * Filters open tasks for the Prep task list based on the selected PrepFilter.
 *
 * - all:     all incomplete tasks (snoozed included)
 * - due:     pending + dueDate within next 14 days (strictly after today)
 * - overdue: status === 'overdue'
 * - blocked: status === 'blocked'
 *
 * Snoozed tasks appear ONLY in 'all', not in due/overdue/blocked.
 */
export function filterOpenTasksForPrep(tasks: Task[], filter: PrepFilter, today: Date): Task[] {
  const todayStr = today.toISOString().slice(0, 10);

  return tasks.filter((task) => {
    const status = getTaskStatus(task, todayStr);
    const isDone = status === 'done';

    switch (filter) {
      case 'all':
        return !isDone;
      case 'due': {
        if (isDone || status !== 'pending') return false;
        const dueDays = getDueDays(task, today);
        return dueDays > 0 && dueDays <= 14;
      }
      case 'overdue':
        return !isDone && status === 'overdue';
      case 'blocked':
        return !isDone && status === 'blocked';
    }
  });
}

/**
 * Counts tasks for each PrepFilter option, for rendering segmented-control badges.
 */
export function countPrepFilters(tasks: Task[], today: Date): Record<PrepFilter, number> {
  return {
    all: filterOpenTasksForPrep(tasks, 'all', today).length,
    due: filterOpenTasksForPrep(tasks, 'due', today).length,
    overdue: filterOpenTasksForPrep(tasks, 'overdue', today).length,
    blocked: filterOpenTasksForPrep(tasks, 'blocked', today).length,
  };
}

/**
 * Sorts tasks by dueDays ascending, then by title as tiebreak.
 * Mutates a copy — does not mutate the input array.
 */
export function sortPrep(tasks: Task[]): Task[] {
  const today = new Date();
  return [...tasks].sort((a, b) => {
    const aDays = getDueDays(a, today);
    const bDays = getDueDays(b, today);
    if (aDays !== bDays) return aDays - bDays;
    return a.title.localeCompare(b.title);
  });
}

/**
 * Groups tasks by TaskCategory, preserving the canonical enum order.
 * Returns a Map with only categories that have at least one task.
 * Empty categories are omitted entirely.
 */
export function groupByCategory(tasks: Task[]): Map<TaskCategory, Task[]> {
  const result = new Map<TaskCategory, Task[]>();

  for (const category of CANONICAL_CATEGORY_ORDER) {
    const matching = tasks.filter((t) => t.category === category);
    if (matching.length > 0) {
      result.set(category, matching);
    }
  }

  return result;
}
